import { useMemo, useState } from 'react'
import {
  AlertTriangle, Building2, CalendarClock, Gavel, MapPin, Plus, Printer, Search, ShieldAlert, Warehouse, Wrench,
} from 'lucide-react'
import { type ColumnDef } from '@tanstack/react-table'
import { PageHeader } from '@/components/layout/page-header'
import { DataTable } from '@/components/ui/data-table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { useEquipment, useEquipmentMeta, useEquipmentStats } from '@/hooks/useEquipment'
import type { Equipment, EquipmentLocationType, EquipmentStatus } from '@/types/equipment'
import { ConditionDot, LocationCell, StatusBadge, WarrantyBadge } from './geraete/EquipmentBadges'
import {
  LOCATION_CONFIG, LOCATION_KEYS, STATUS_CONFIG, STATUS_KEYS, daysText, formatDateTime, formatIsoDate,
} from './geraete/equipmentUtils'
import GeraetDetailModal from './geraete/GeraetDetailModal'
import CreateEquipmentDialog from './geraete/CreateEquipmentDialog'
import LabelDialog from './geraete/LabelDialog'
import DefektInbox from './geraete/DefektInbox'
import OrteVerwaltung from './geraete/OrteVerwaltung'

type Tab = 'geraete' | 'entscheidungen' | 'orte'

// KPI-Kacheln: Klick setzt den passenden Filter
type QuickFilter =
  | { kind: 'status'; value: EquipmentStatus }
  | { kind: 'location'; value: EquipmentLocationType }
  | { kind: 'openDefects' }
  | null

const selectCls = 'h-9 rounded-md border border-input bg-white px-2 text-sm'

export default function Geraete() {
  const [tab, setTab] = useState<Tab>('geraete')
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('')
  const [locationType, setLocationType] = useState('')
  const [category, setCategory] = useState('')
  const [openDefects, setOpenDefects] = useState(false)
  const [includeRetired, setIncludeRetired] = useState(false)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [labelOpen, setLabelOpen] = useState(false)
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({})

  const params = useMemo(() => ({
    status: status || undefined,
    locationType: locationType || undefined,
    category: category || undefined,
    openDefects: openDefects ? '1' : undefined,
    includeRetired: includeRetired ? '1' : undefined,
  }), [status, locationType, category, openDefects, includeRetired])

  const { data: items = [], isLoading } = useEquipment(params)
  const { data: stats } = useEquipmentStats()
  const { data: meta } = useEquipmentMeta()

  // Suche clientseitig — Liste ist klein (Maschinenpark, nicht Artikelstamm)
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return items
    return items.filter((e) =>
      [e.unique_code, e.name, e.manufacturer, e.model, e.serial_number, e.location_label, e.category]
        .some((v) => v && String(v).toLowerCase().includes(needle)))
  }, [items, q])

  const selectedItems = useMemo(
    () => items.filter((e) => rowSelection[String(e.id)]),
    [items, rowSelection],
  )

  const applyQuick = (f: QuickFilter) => {
    setTab('geraete')
    setStatus(f?.kind === 'status' ? f.value : '')
    setLocationType(f?.kind === 'location' ? f.value : '')
    setOpenDefects(f?.kind === 'openDefects')
    if (f?.kind === 'status' && f.value === 'ausgemustert') setIncludeRetired(true)
  }

  const columns = useMemo<ColumnDef<Equipment>[]>(() => [
    {
      id: 'select',
      size: 40,
      enableSorting: false,
      header: ({ table }) => (
        <input
          type="checkbox"
          aria-label="Alle auswählen"
          className="h-4 w-4 rounded border-gray-300"
          checked={table.getIsAllPageRowsSelected()}
          ref={(el) => { if (el) el.indeterminate = table.getIsSomePageRowsSelected() && !table.getIsAllPageRowsSelected() }}
          onChange={(e) => table.toggleAllPageRowsSelected(e.target.checked)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          aria-label="Für Etikettendruck auswählen"
          className="h-4 w-4 rounded border-gray-300"
          checked={row.getIsSelected()}
          onChange={row.getToggleSelectedHandler()}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      accessorKey: 'unique_code',
      header: 'Nr.',
      cell: ({ row }) => <span className="font-mono text-sm font-bold text-primary-700">{row.original.unique_code}</span>,
    },
    {
      accessorKey: 'name',
      header: 'Gerät',
      cell: ({ row }) => {
        const e = row.original
        return (
          <div className="flex min-w-0 items-center gap-2">
            {e.thumb_path
              ? <img src={e.thumb_path} alt="" className="h-9 w-9 shrink-0 rounded object-cover" />
              : <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-gray-100"><Wrench className="h-4 w-4 text-gray-400" /></div>}
            <div className="min-w-0">
              <p className="truncate font-medium">{e.name}</p>
              <p className="truncate text-xs text-muted-foreground">{[e.manufacturer, e.model].filter(Boolean).join(' ') || e.category}</p>
            </div>
          </div>
        )
      },
    },
    { accessorKey: 'category', header: 'Kategorie', cell: ({ row }) => <span className="text-sm">{row.original.category}</span> },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      accessorKey: 'location_label',
      header: 'Standort',
      cell: ({ row }) => <LocationCell item={row.original} />,
    },
    {
      accessorKey: 'condition',
      header: 'Zustand',
      cell: ({ row }) => <ConditionDot condition={row.original.condition} />,
    },
    {
      id: 'warranty',
      header: 'Garantie',
      accessorFn: (e) => e.warranty_effective_until ?? '',
      cell: ({ row }) => <WarrantyBadge state={row.original.warranty_state} until={row.original.warranty_effective_until} />,
    },
    {
      accessorKey: 'dguv_next_check',
      header: 'DGUV',
      cell: ({ row }) => {
        const e = row.original
        if (!e.dguv_next_check) return <span className="text-gray-400">–</span>
        const overdue = e.dguv_days_left != null && e.dguv_days_left < 0
        const soon = e.dguv_days_left != null && e.dguv_days_left <= 30
        return (
          <span className={cn('text-sm', overdue ? 'font-semibold text-red-600' : soon ? 'text-amber-600' : '')} title={daysText(e.dguv_days_left)}>
            {formatIsoDate(e.dguv_next_check)}
          </span>
        )
      },
    },
    {
      accessorKey: 'last_seen_at',
      header: 'Zuletzt gesehen',
      cell: ({ row }) => row.original.last_seen_at
        ? <span className="text-sm text-muted-foreground">{formatDateTime(row.original.last_seen_at)}</span>
        : <span className="text-gray-300">–</span>,
    },
  ], [])

  const kpi = (label: string, value: number | undefined, icon: React.ReactNode, onClick: () => void, tone = '') => (
    <button onClick={onClick} className={cn('rounded-lg border bg-white p-3 text-left transition-shadow hover:shadow-sm', tone)}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">{icon}{label}</div>
      <p className="mt-1 text-2xl font-bold">{value ?? <Skeleton className="h-7 w-10" />}</p>
    </button>
  )

  const decisionsCount = (stats?.openDefects ?? 0) + (stats?.disposalPending ?? 0)

  return (
    <>
    <div className="space-y-4">
      <PageHeader
        title="Geräte & Maschinen"
        description="Standort, Zustand, Gewährleistung und Defekte aller Maschinen — Bauleiter buchen per Etikett-Scan in der PWA."
        actions={
          <>
            {selectedItems.length > 0 && (
              <Button variant="outline" onClick={() => setLabelOpen(true)}><Printer className="h-4 w-4" /> Etiketten ({selectedItems.length})</Button>
            )}
            <Button onClick={() => setCreateOpen(true)}><Plus className="h-4 w-4" /> Neues Gerät</Button>
          </>
        }
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {kpi('Im Lager', stats?.byLocation.lager, <Warehouse className="h-4 w-4" />, () => applyQuick({ kind: 'location', value: 'lager' }))}
        {kpi('Im Einsatz', stats?.byStatus.im_einsatz, <Building2 className="h-4 w-4" />, () => applyQuick({ kind: 'status', value: 'im_einsatz' }))}
        {kpi('Offene Defekte', stats?.openDefects, <AlertTriangle className="h-4 w-4 text-red-500" />, () => setTab('entscheidungen'),
          stats?.openDefects ? 'border-red-200' : '')}
        {kpi('Aussonderung bestätigen', stats?.disposalPending, <Gavel className="h-4 w-4 text-rose-500" />, () => setTab('entscheidungen'),
          stats?.disposalPending ? 'border-rose-300 bg-rose-50' : '')}
        {kpi('Garantie läuft ab (60 T.)', stats?.warrantyExpiring, <ShieldAlert className="h-4 w-4 text-amber-500" />, () => applyQuick(null))}
        {kpi('DGUV fällig', (stats?.dguvOverdue ?? 0) + (stats?.dguvUpcoming ?? 0), <CalendarClock className="h-4 w-4 text-indigo-500" />, () => applyQuick(null),
          stats?.dguvOverdue ? 'border-red-200' : '')}
      </div>

      <div className="flex gap-1 border-b">
        {([
          ['geraete', 'Geräte', stats?.total],
          ['entscheidungen', 'Entscheidungen', decisionsCount],
          ['orte', 'Lager & Fahrzeuge', undefined],
        ] as [Tab, string, number | undefined][]).map(([key, label, count]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={cn(
              '-mb-px flex items-center gap-1.5 border-b-2 px-4 py-2 text-sm font-medium',
              tab === key ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-800',
            )}
          >
            {label}
            {count != null && count > 0 && (
              <span className={cn('rounded-full px-1.5 text-xs', key === 'entscheidungen' ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-600')}>{count}</span>
            )}
          </button>
        ))}
      </div>

      {tab === 'geraete' && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-full max-w-xs">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nr., Name, Seriennr., Standort…" className="pl-8" />
            </div>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls}>
              <option value="">Alle Status</option>
              {STATUS_KEYS.map((s) => <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>)}
            </select>
            <select value={locationType} onChange={(e) => setLocationType(e.target.value)} className={selectCls}>
              <option value="">Alle Standorte</option>
              {LOCATION_KEYS.map((l) => <option key={l} value={l}>{LOCATION_CONFIG[l].label}</option>)}
            </select>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className={selectCls}>
              <option value="">Alle Kategorien</option>
              {meta?.categories.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
            <label className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <input type="checkbox" checked={openDefects} onChange={(e) => setOpenDefects(e.target.checked)} className="h-4 w-4 rounded" />
              nur mit Defekt
            </label>
            <label className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <input type="checkbox" checked={includeRetired} onChange={(e) => setIncludeRetired(e.target.checked)} className="h-4 w-4 rounded" />
              ausgemusterte zeigen
            </label>
            {(status || locationType || category || openDefects || q) && (
              <Button variant="ghost" size="sm" onClick={() => { setQ(''); applyQuick(null); setCategory('') }}>Filter zurücksetzen</Button>
            )}
          </div>

          {isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            <DataTable
              columns={columns}
              data={filtered}
              pageSize={50}
              onRowClick={(row) => setSelectedId(row.id)}
              getRowClassName={(row) => cn(
                'cursor-pointer',
                row.status === 'ausgemustert' && 'opacity-50',
                row.status === 'aussonderung_beantragt' && 'bg-rose-50/60',
                row.status === 'defekt' && 'bg-red-50/60',
              )}
              enableRowSelection
              getRowId={(row) => String(row.id)}
              rowSelection={rowSelection}
              onRowSelectionChange={setRowSelection}
              emptyText={items.length ? 'Keine Treffer' : 'Noch keine Geräte — oben rechts „Neues Gerät"'}
            />
          )}
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <MapPin className="h-3.5 w-3.5" /> Zeilen anhaken → „Etiketten" druckt die Code-Aufkleber für den Brother VC-500W (9/12 mm).
          </p>
        </>
      )}

      {tab === 'entscheidungen' && <DefektInbox onOpenEquipment={setSelectedId} />}
      {tab === 'orte' && <OrteVerwaltung />}
    </div>

      {/* Modals AUSSERHALB von space-y-4 — sonst bekommt das fixed-Backdrop dessen margin-top ab */}
      <GeraetDetailModal equipmentId={selectedId} onClose={() => setSelectedId(null)} />
      <CreateEquipmentDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(item) => { setCreateOpen(false); setSelectedId(item.id) }}
      />
      <LabelDialog items={selectedItems} open={labelOpen} onClose={() => setLabelOpen(false)} />
    </>
  )
}
